import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { Pressable, View } from 'react-native';
import { PRICING_BREADCRUMBS_CHEVRON_SIZE } from '@sales-portal/domain/pricing/constants';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useNavigation } from '@react-navigation/native';

// Parent list screens a section can link back to (org-scoped, no params).
export type PricingBreadcrumbsParentRoute =
  | SALES_PORTAL_ROUTES.PricingAgentTasks
  | SALES_PORTAL_ROUTES.PricingCustomers
  | SALES_PORTAL_ROUTES.PricingManagement;

// Every route a crumb can link to: a section's parent list, plus the hub the
// root crumb points at.
type PricingBreadcrumbsRoute =
  | PricingBreadcrumbsParentRoute
  | SALES_PORTAL_ROUTES.PricingOverview;

interface Props {
  parentRoute?: PricingBreadcrumbsParentRoute;
  // Omitted on the hub, which is the root: the trail is then just "Pricing".
  secondaryBreadcrumb?: string;
  tertiaryBreadcrumb?: string;
}

export const PricingBreadcrumbs: React.FC<Props> = ({
  parentRoute,
  secondaryBreadcrumb,
  tertiaryBreadcrumb,
}) => {
  const [styles] = useStyles();

  // With nothing after it, the root crumb *is* the current page, so it renders
  // as plain text; otherwise it links to the hub.
  if (!secondaryBreadcrumb) {
    return (
      <View style={styles.breadcrumbs}>
        <Crumb current label={i18n.t('pricing')} />
      </View>
    );
  }

  return (
    <View style={styles.breadcrumbs}>
      <BreadcrumbLink
        label={i18n.t('pricing')}
        route={SALES_PORTAL_ROUTES.PricingOverview}
        testID="pricing-breadcrumbs-root-link"
      />
      <Chevron />
      {tertiaryBreadcrumb && parentRoute ? (
        <BreadcrumbLink label={secondaryBreadcrumb} route={parentRoute} />
      ) : (
        <Crumb current={!tertiaryBreadcrumb} label={secondaryBreadcrumb} />
      )}
      {tertiaryBreadcrumb ? (
        <>
          <Chevron />
          <Crumb current label={tertiaryBreadcrumb} />
        </>
      ) : null}
    </View>
  );
};

const Crumb: React.FC<{ current?: boolean; label: string }> = ({
  current,
  label,
}) => {
  const theme = useTheme();

  return (
    <Typography
      color={
        current ? theme.sk.color.text.primary : theme.sk.color.text.secondary
      }
      variant="paragraph-md"
    >
      {label}
    </Typography>
  );
};

const Chevron: React.FC = () => {
  const theme = useTheme();

  return (
    <ChevronRightThinIcon
      height={PRICING_BREADCRUMBS_CHEVRON_SIZE}
      stroke={theme.sk.color.icon.tertiary}
      width={PRICING_BREADCRUMBS_CHEVRON_SIZE}
    />
  );
};

// The root and section links carry distinct testIDs so a selector can't match
// both.
const BreadcrumbLink: React.FC<{
  label: string;
  route: PricingBreadcrumbsRoute;
  testID?: string;
}> = ({ label, route, testID = 'pricing-breadcrumbs-link' }) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalStackParams>>();
  const theme = useTheme();

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={() => navigation.navigate(route)}
      testID={testID}
    >
      <Typography
        color={
          isHovered
            ? theme.sk.color.text.primary
            : theme.sk.color.text.secondary
        }
        variant="paragraph-md"
      >
        {label}
      </Typography>
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  breadcrumbs: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
